import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  resetEngagement,
  trackPageExit,
  trackScrollDepth,
} from "../utils/analytics";

const SCROLL_MILESTONES = [25, 50, 75, 90];

function getScrollPercent() {
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  const scrollHeight =
    document.documentElement.scrollHeight - window.innerHeight;
  if (scrollHeight <= 0) return 100;
  return Math.min(100, Math.round((scrollTop / scrollHeight) * 100));
}

export function useEngagementTracking() {
  const { pathname } = useLocation();
  const pageStartRef = useRef(Date.now());
  const maxScrollRef = useRef(0);
  const firedMilestonesRef = useRef(new Set());
  const exitSentRef = useRef(false);

  const sendPageExit = () => {
    if (exitSentRef.current) return;
    exitSentRef.current = true;
    const timeOnPageSeconds = Math.round(
      (Date.now() - pageStartRef.current) / 1000
    );
    trackPageExit({
      timeOnPageSeconds,
      maxScrollPercent: maxScrollRef.current,
    });
  };

  useEffect(() => {
    pageStartRef.current = Date.now();
    maxScrollRef.current = 0;
    firedMilestonesRef.current = new Set();
    exitSentRef.current = false;
    resetEngagement();

    const handleScroll = () => {
      const percent = getScrollPercent();
      maxScrollRef.current = Math.max(maxScrollRef.current, percent);

      SCROLL_MILESTONES.forEach((milestone) => {
        if (
          percent >= milestone &&
          !firedMilestonesRef.current.has(milestone)
        ) {
          firedMilestonesRef.current.add(milestone);
          trackScrollDepth(milestone);
        }
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendPageExit();
      }
    };

    const handlePageHide = () => {
      sendPageExit();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    handleScroll();

    return () => {
      sendPageExit();
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [pathname]);
}
